import React, { useState } from 'react';
import { Handle, Position } from 'reactflow';

const CustomNode = ({ data }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isClicked, setIsClicked] = useState(false);

  const convert_to_readable = (differenceInMs) => {
    const differenceInSeconds = Math.floor(differenceInMs / 1000);
    const differenceInMinutes = Math.floor(differenceInSeconds / 60);
    const differenceInHours = Math.floor(differenceInMinutes / 60);
    const differenceInDays = Math.floor(differenceInHours / 24);

    const remainingHours = differenceInHours % 24;
    const remainingMinutes = differenceInMinutes % 60;
    const remainingSeconds = differenceInSeconds % 60;

    if (differenceInDays > 0) {
      return `${differenceInDays}d ${remainingHours}h`;
    } else if (differenceInHours > 0) {
      return `${differenceInHours}h ${remainingMinutes}m`;
    } else if (differenceInMinutes > 0) {
      return `${differenceInMinutes}m ${remainingSeconds}s`;
    } else if (remainingSeconds > 0) {
      return `${remainingSeconds}s`;
    } else {
      return `${differenceInMs}ms`;
    }
  };

  const isInactive = (parseInt(data.timeSpentFirstOccurance) < 0);
  const totalTimeSpent = data.timeSpentFirstOccurance + (data.timeSpentRemainingOccurance > 0 ? data.timeSpentRemainingOccurance : 0);

  // Calculate the diameter proportional to the square root of the total time spent
  const baseDiameter = 25; // Minimum size for nodes with zero or very little time spent
  const diameter = totalTimeSpent > 0 ? baseDiameter + Math.sqrt(totalTimeSpent) * 0.0035 : baseDiameter;
  data.sz = 2 * diameter;

  const pieChart = data.timeSpentRemainingOccurance > 0;
  const remainingOccurrencePercentage = data.timeSpentRemainingOccurance / totalTimeSpent;

  // Handlers for showing and hiding the tooltip
  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    if (!isClicked) setShowTooltip(false); // Hide only if not clicked
  };

  const handleClick = () => {
    setIsClicked(!isClicked); // Toggle the tooltip on click
    setShowTooltip(!isClicked); // Show or hide based on click
  };

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: '50%',
        width: `${diameter}px`,
        height: `${diameter}px`,
        background: isInactive ? '#e0e0e0' : (data.repo ? '#f1f8ff' : '#5A9FBE'),
        boxShadow: isInactive ? 'none' : '0 4.8px 7.2px rgba(0, 0, 0, 0.15)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        cursor: isInactive ? 'default' : 'pointer',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'visible', // Allow text overflow to be visible
        color: isInactive ? '#808080' : (data.repo ? '#333' : '#fff'),
        border: isInactive ? '1.2px solid #bbb' : '1.5px solid #ccc',
      }}
      className="custom-node"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {/* If node has remaining occurrences, display a pie chart */}
      {pieChart ? (
        <svg width="100%" height="100%" viewBox="0 0 32 32" style={{ position: 'absolute', borderRadius: '50%' }}>
          <circle r="16" cx="16" cy="16" fill={data.repo ? '#f1f8ff' : '#5A9FBE'} />
          <circle
            r="16"
            cx="16"
            cy="16"
            fill="transparent"
            stroke="#A8D8B9" // Color for remaining occurrence
            strokeWidth="32"
            strokeDasharray={`${remainingOccurrencePercentage * 100} ${100 - remainingOccurrencePercentage * 100}`}
            strokeDashoffset="25"
          />
        </svg>
      ) : null}

      <div
        style={{
          textAlign: 'center',
          fontSize: '24px', // Dynamically adjust the font size based on node size
          fontWeight: 'bold',
          zIndex: '100',
          color: 'black',
          whiteSpace: 'nowrap', // Prevent the text from wrapping
          overflow: 'visible', // Ensure text overflows when the node is too small
        }}
      >
        {data.state}
        {!isInactive && (
          <div style={{ fontSize: '20px', fontWeight: 'normal' }}>
            {`${convert_to_readable(totalTimeSpent)}`}
          </div>
        )}
      </div>

      {/* Tooltip to display time details */}
      {showTooltip && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: `${diameter + 10}px`,
            transform: 'translateY(-50%)',
            background: 'rgba(0, 0, 0, 0.75)',
            color: 'white',
            padding: '5px 10px',
            borderRadius: '4px',
            fontSize: '18px',
            whiteSpace: 'nowrap',
            zIndex: '1000',
          }}
        >
          {isInactive ? <div>Not Reached</div> : (<div>Total Time Spent: {convert_to_readable(totalTimeSpent)}</div>)}
          {data.timeSpentRemainingOccurance > 0 && (<div>
            <div>First Pass: {convert_to_readable(data.timeSpentFirstOccurance)}</div>
            <div>Rework: {convert_to_readable(data.timeSpentRemainingOccurance)}</div>
          </div>
          )}
        </div>
      )}

      {/* Handles for connections */}
      <Handle type="target" position={Position.Top} style={{ backgroundColor: 'transparent' }} />
      <Handle type="source" position={Position.Bottom} style={{ backgroundColor: 'transparent' }} />
      <Handle type="target" position={Position.Right} id="right" style={{ backgroundColor: 'transparent' }} />
      <Handle type="source" position={Position.Right} id="right" style={{ backgroundColor: 'transparent' }} />
      <Handle type="target" position={Position.Left} id="left" style={{ backgroundColor: 'transparent' }} />
      <Handle type="source" position={Position.Left} id="left" style={{ backgroundColor: 'transparent' }} />

      <style>
        {`
          .custom-node:hover {
            transform: scale(1.05);
            box-shadow: 0 6px 10px rgba(0, 0, 0, 0.2);
          }
        `}
      </style>
    </div>
  );
};

export default CustomNode;
